/**
 * ListPagination - Standardized pagination controls for lists
 * Phase 3.5: List Pagination & Search Standardization
 * 
 * Features:
 * - Page navigation (prev/next + page numbers)
 * - Page size selector (15/25/50/100)
 * - "Showing X-Y of Z" text
 * - Loading state support
 * - Mobile responsive
 */
import React from 'react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader2 } from 'lucide-react';

export const ListPagination = ({
  page,
  pageSize,
  total,
  totalPages,
  onPageChange,
  onPageSizeChange,
  loading = false,
  pageSizeOptions = [15, 25, 50, 100],
  showPageSize = true,
  className = '',
}) => {
  // Calculate showing range
  const startItem = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      // Calculate middle pages
      let start = Math.max(2, page - 1);
      let end = Math.min(totalPages - 1, page + 1);
      
      // Adjust if at edges
      if (page <= 2) {
        end = 4;
      } else if (page >= totalPages - 1) {
        start = totalPages - 3;
      }
      
      // Add ellipsis if needed before middle pages
      if (start > 2) {
        pages.push('...');
      }
      
      // Add middle pages
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      // Add ellipsis if needed after middle pages
      if (end < totalPages - 1) {
        pages.push('...');
      }
      
      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const handlePrevious = () => {
    if (page > 1) {
      onPageChange(page - 1);
    }
  };

  const handleNext = () => {
    if (page < totalPages) {
      onPageChange(page + 1);
    }
  };

  const handleFirst = () => {
    if (page > 1) {
      onPageChange(1);
    }
  };

  const handleLast = () => {
    if (page < totalPages) {
      onPageChange(totalPages);
    }
  };

  // Don't render if no data
  if (total === 0 && !loading) {
    return null;
  }

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 py-4 ${className}`}>
      {/* Left side: Showing X-Y of Z */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <span data-testid="pagination-info">
            Showing {startItem}–{endItem} of {(total || 0).toLocaleString()}
          </span>
        )}
      </div>

      {/* Right side: Page size selector + Navigation */}
      <div className="flex items-center gap-4">
        {/* Page size selector */}
        {showPageSize && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground hidden sm:inline">Per page:</span>
            <Select 
              value={pageSize.toString()} 
              onValueChange={(val) => onPageSizeChange(parseInt(val))}
            >
              <SelectTrigger className="w-[70px] h-8" data-testid="page-size-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map(size => (
                  <SelectItem key={size} value={size.toString()}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Page navigation */}
        <div className="flex items-center gap-1">
          {/* First page */}
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 hidden sm:flex"
            onClick={handleFirst}
            disabled={page === 1 || loading}
            aria-label="First page"
            data-testid="pagination-first"
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>

          {/* Previous */}
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={handlePrevious}
            disabled={page === 1 || loading}
            aria-label="Previous page"
            data-testid="pagination-prev"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* Page numbers - hidden on mobile */}
          <div className="hidden sm:flex items-center gap-1">
            {getPageNumbers().map((pageNum, idx) => (
              pageNum === '...' ? (
                <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground">...</span>
              ) : (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? 'default' : 'outline'}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onPageChange(pageNum)}
                  disabled={loading}
                  data-testid={`pagination-page-${pageNum}`}
                >
                  {pageNum}
                </Button>
              )
            ))}
          </div>

          {/* Mobile: Page indicator */}
          <span className="sm:hidden text-sm px-2">
            {page} / {totalPages}
          </span>

          {/* Next */}
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={handleNext}
            disabled={page === totalPages || loading}
            aria-label="Next page"
            data-testid="pagination-next"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          {/* Last page */}
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 hidden sm:flex"
            onClick={handleLast}
            disabled={page === totalPages || loading}
            aria-label="Last page"
            data-testid="pagination-last"
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ListPagination;
